import { FC, useContext, MouseEvent } from 'react';
import { useSelector } from 'react-redux';
import { isTravelTimeSearchSelector } from '../../reducers/filters';
import { SearchContext } from '../SearchContext';
import triggerTracking from '@blueprint/tracking';
import { Icon } from '@blueprint/ui';
import {
    AlertPanelWrapper,
    AlertPanelButtonLink,
    LeftAlignButtonWithIcon,
    PaddedDivider
} from './AlertPanel.styles';
import { AlertPanelProps } from './AlertPanel.types';

const createAlertBtnText = 'Create email alert';
const saveSearchBtnText = 'Save this search';
const travelTimeSearchBtnText = 'Travel time search';
const drawYourSearchBtnText = 'Draw your search';

const fireTrackingEvent = (label: string) => {
    triggerTracking({
        event: 'GAEvent',
        eventCategory: 'search results',
        eventAction: 'clickthrough',
        eventLabel: label
    });
};

const AlertPanelAjax: FC<AlertPanelProps> = ({
    links,
    onAjaxLinkClick,
    onSimpleLinkClick
}) => {
    const {
        state: { userAlertId }
    } = useContext(SearchContext);

    const isSavedSearch = userAlertId !== null;
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const simpleLinkTravelTime = (evt: MouseEvent<HTMLElement>) => {
        onSimpleLinkClick &&
            onSimpleLinkClick(evt, 'search results', 'travel_time_search');
    };

    const ajaxLinkCreateAlert = async (evt: MouseEvent<HTMLElement>) => {
        onAjaxLinkClick &&
            (await onAjaxLinkClick(evt, 'search results', 'create_alert'));
    };

    const ajaxLinkSaveSearch = async (evt: MouseEvent<HTMLElement>) => {
        onAjaxLinkClick &&
            (await onAjaxLinkClick(evt, 'search results', 'save_search'));
    };

    return (
        <AlertPanelWrapper>
            {!isSavedSearch ? (
                <>
                    <AlertPanelButtonLink
                        variant="secondary"
                        data-testid="create-email-alert-button"
                        data-url={links?.createAlert || ''}
                        isFullWidth
                        onClick={ajaxLinkCreateAlert}
                    >
                        <Icon iconId="email_outline" />
                        {createAlertBtnText}
                    </AlertPanelButtonLink>
                    <AlertPanelButtonLink
                        variant="secondary"
                        data-testid="save-search-button"
                        data-url={links?.saveSearch || ''}
                        isFullWidth
                        onClick={ajaxLinkSaveSearch}
                    >
                        <Icon iconId="save_outline" />
                        {saveSearchBtnText}
                    </AlertPanelButtonLink>
                </>
            ) : null}
            <PaddedDivider variant="horizontal" />
            {!isTravelTimeSearch ? (
                <LeftAlignButtonWithIcon
                    variant="tertiary"
                    data-testid="travel-time-search-button-link"
                    data-url="/travel-time/"
                    isFullWidth
                    onClick={simpleLinkTravelTime}
                >
                    <Icon iconId="history" />
                    {travelTimeSearchBtnText}
                </LeftAlignButtonWithIcon>
            ) : null}
            <LeftAlignButtonWithIcon
                variant="tertiary"
                data-testid="draw-your-search-button-link"
                href={links?.mapView || ''}
                rel="nofollow"
                onClick={() => fireTrackingEvent('draw_search')}
                isFullWidth
            >
                <Icon iconId="draw" />
                {drawYourSearchBtnText}
            </LeftAlignButtonWithIcon>
        </AlertPanelWrapper>
    );
};

export default AlertPanelAjax;
